import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AttendanceData } from '../../data/mockData';

interface AttendanceChartProps {
    data: AttendanceData[];
}

interface EKYCChartProps {
    ekycDone: number;
    ekycNotDone: number;
}

export const AttendanceChart: React.FC<AttendanceChartProps> = ({ data }) => {
    const formatXAxis = (tickItem: string) => {
        const date = new Date(tickItem);
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    };

    const formatTooltipValue = (value: number) => {
        return value.toLocaleString();
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <Card>
                <CardHeader>
                    <CardTitle>Monthly Attendance Trends</CardTitle>
                    <CardDescription>Total attendance vs Face Authentication attendance over time</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="date" tickFormatter={formatXAxis} stroke="#666" fontSize={12} />
                                <YAxis stroke="#666" fontSize={12} tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    }}
                                    labelFormatter={(label) => `Month: ${formatXAxis(label)}`}
                                    formatter={(value: number, name: string) => [formatTooltipValue(value), name === 'attendance' ? 'Total Attendance' : 'Face Auth Attendance']}
                                />
                                <Legend />
                                <Bar dataKey="attendance" fill="#3b82f6" name="Total Attendance" radius={[2, 2, 0, 0]} />
                                <Bar dataKey="faceAuthAttendance" fill="#10b981" name="Face Auth Attendance" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export const EKYCChart: React.FC<EKYCChartProps> = ({ ekycDone, ekycNotDone }) => {
    const data = [
        { name: 'eKYC Done', value: ekycDone, color: '#10b981' },
        { name: 'eKYC Not Done', value: ekycNotDone, color: '#f59e0b' },
    ];

    const formatTooltipValue = (value: number) => {
        return value.toLocaleString();
    };

    const renderCustomLabel = (entry: any) => {
        const total = ekycDone + ekycNotDone;
        const percent = ((entry.value / total) * 100).toFixed(1);
        return `${percent}%`;
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
            <Card>
                <CardHeader>
                    <CardTitle>eKYC Status Distribution</CardTitle>
                    <CardDescription>Breakdown of eKYC completion status across all beneficiaries</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={data} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={100} fill="#8884d8" dataKey="value">
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    }}
                                    formatter={(value: number) => [formatTooltipValue(value), 'Count']}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};
